import {
    ChangeDetectionStrategy,
    Component,
    HostBinding,
    Injector,
    inject,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { FiFormService } from '@fi-app/fi-form/fi-form.service';
import { ObjectTypes, SideListOptionType } from '@fi-shared/types';
import { StateAccessCheckService } from '@fi-shared/fi-shared-services/state-access-check.service';
import { takeUntil } from 'rxjs/operators';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { IItemsListAction2, ISideListOptionsItem } from '@fi-shared/fi-items-list/fi-items-list';
import { FiProfileSizes } from '@fi-shared/profile-icon/profile-icon.component';
import { FiRemoteStateFullDataSource } from '@fi-shared/fi-items-list/fi-remote-state-full-data-source';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';
import { ILoadDataArgs } from '@fi-shared/fi-items-list/fi-data-source-base.directive';
import { IForm } from '@fi-shared/fi-form-shared/fi-form-shared';
import { FiMobileAppHelperService } from '@fi-app/utilities/fi-services/mobile-app-helper.service';
import { FiDateFormatOptions } from '@fi-shared/utils';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { FiNotificationHelperService } from '@fi-shared/fi-notification/fi-notification-helper.service';
import { FiFormActionsService } from '@fi-app/fi-form/fi-form-actions.service';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { FiFormHelperService } from '@fi-shared/fi-form-shared/fi-form-helper.service';

@Component({
    selector: 'fi-form-list',
    templateUrl: './fi-form-list.component.html',
    styleUrls: ['./fi-form-list.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-form-list' },
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0', border: 'none' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    standalone: false,
})
export class FiFormListComponent extends FiRemoteStateFullDataSource<IForm> implements OnInit {
    public readonly defaultSortColumnName = 'PublicationStartDate';
    public readonly defaultSortDirection = true;
    public readonly objectType = ObjectTypes.Form;
    public readonly itemsListType = FiItemsListType.FormListData;
    public readonly defaultFilterType = SideListOptionType.FormsToAnswer;

    private formService = inject(FiFormService);
    private formHelperService = inject(FiFormHelperService);
    private stateAccessCheckService = inject(StateAccessCheckService);
    private fiFormatDateService = inject(FiFormatDateService);
    private fiNotificationHelperService = inject(FiNotificationHelperService);
    private fiFormActionsService = inject(FiFormActionsService);
    private detectDeviceService = inject(DetectDevicesService);

    protected readonly sideListOptions: ReadonlyArray<ISideListOptionsItem> = [
        { value: SideListOptionType.FormsToAnswer, text: this.appTextService.get('form.forms_to_answer') },
        { value: SideListOptionType.AnsweredForms, text: this.appTextService.get('form.answered_forms') },
        { value: SideListOptionType.CreatedBy, text: this.appTextService.get('common.created_by_me') },
        { value: SideListOptionType.Deleted, text: this.appTextService.get('common.trash') },
    ];

    protected readonly formAccessRight: boolean = this.stateAccessCheckService.isStateAllowedForCurrentUser(
        FiStateNames.Form.Add,
    );
    protected actions: IItemsListAction2<IForm>[] = [];
    protected topActions: IItemsListAction2<IForm>[] = [];

    @HostBinding('class.mobile-view') get isMobileView(): boolean {
        return this.detectDeviceService.isMobilePhone();
    }

    constructor(injector: Injector) {
        super(injector);
    }

    public ngOnInit(): void {
        this.reloadListSubscription();
        this.initTopActions();
        this.initOptionsColumnActions(); // actions at options column
    }

    protected showTopActions(): boolean {
        return this.formAccessRight && !this.extendedSearchMode;
    }

    private initTopActions(): void {
        const itemsListMethods = this.getPublicMethods();
        const deleteAction: IItemsListAction2<IForm> = {
            title: this.appTextService.get('common.delete'),
            condition: () => this.filterType !== SideListOptionType.Deleted,
            onClick: () => this.fiFormActionsService.doDeleteForms(this.selectedItems(), itemsListMethods),
        };
        const deletePermanentlyAction: IItemsListAction2<IForm> = {
            title: this.appTextService.get('common.delete_permanently'),
            condition: () => this.filterType === SideListOptionType.Deleted,
            onClick: () => this.fiFormActionsService.doDeleteForms(this.selectedItems(), itemsListMethods),
        };
        const restoreAction: IItemsListAction2<IForm> = {
            title: this.appTextService.get('common.restore'),
            condition: () => this.filterType === SideListOptionType.Deleted,
            onClick: () => this.fiFormActionsService.doRestoreForms(this.selectedItems(), itemsListMethods),
        };
        this.topActions = [deleteAction, restoreAction, deletePermanentlyAction];
    }

    private initOptionsColumnActions(): void {
        this.actions = [];
        if (this.formAccessRight) {
            const itemsListMethods = this.getPublicMethods();
            const deleteAction: IItemsListAction2<IForm> = {
                title: this.appTextService.get('form.delete_form'),
                disabled: (form) => !this.formHelperService.isEditAllowedByModuleRights() || !form.HasEditAccess,
                onClick: (form) => this.fiFormActionsService.doDeleteForms<IForm>([form], itemsListMethods),
            };

            const restoreAction: IItemsListAction2<IForm> = {
                title: this.appTextService.get('common.restore'),
                condition: (form) =>
                    this.formHelperService.isEditAllowedByModuleRights() && form.HasEditAccess && form.IsDeleted,
                onClick: (form) => this.fiFormActionsService.doRestoreForms([form], itemsListMethods),
            };

            const deleteAnswersAction: IItemsListAction2<IForm> = {
                title: this.appTextService.get('checklist.delete_responses'),
                disabled: (form) => {
                    return (
                        form.AnswerSetsCount === 0 ||
                        !this.formHelperService.isEditAllowedByModuleRights() ||
                        !form.HasEditAccess
                    );
                },
                onClick: (form) => {
                    this.fiFormActionsService.doDeleteFormAnswers([form], form.AnswerSetsCount).then((r) => {
                        if (r.result) {
                            form.AnswerSetsCount = 0;
                            if (form.AnswerSets) {
                                form.AnswerSets.length = 0;
                            }
                        }
                    });
                },
            };

            this.actions = [
                this.fiFormActionsService.getViewReportAction(),
                this.fiFormActionsService.getEditFormAction(),
                deleteAction,
                restoreAction,
                deleteAnswersAction,
            ];
        }
    }

    private reloadListSubscription(): void {
        this.formService.reloadFormsList$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
            this.reloadData();
        });
    }

    public loadListDataFn(loadArgs: ILoadDataArgs) {
        const parameter = this.getItemsListParameter(loadArgs);
        parameter.FetchParams = {
            FetchItems: true,
            FetchTotalCount: !loadArgs.fetchOnlyItems,
            FetchLightWeightObjects: !loadArgs.fetchOnlyItems,
        };
        return this.formService.getFormsListData(parameter);
    }

    protected onFormNameClick($event: MouseEvent, form: IForm): void {
        $event.preventDefault();
        if (this.extendedSearchMode) {
            return;
        }

        const mobileAppHelperService = this.injector.get(FiMobileAppHelperService);
        if (mobileAppHelperService.mobileAppMode) {
            mobileAppHelperService.openFormViewer(form.Id, form.Name);
        } else {
            this.fiStateService.go(FiStateNames.Form.View, { id: form.Id });
        }
    }

    protected getEndDateText(form: IForm): string {
        if (!form.EndDate) {
            return this.appTextService.get('form.continuous_sharing');
        }
        return this.fiFormatDateService.formatDate(new Date(form.EndDate), FiDateFormatOptions);
    }

    protected isNotVisited(form: IForm): boolean {
        return this.fiNotificationHelperService.isNotVisited(form);
    }

    protected showOptionsColumn(): boolean {
        return this.filterType !== SideListOptionType.AnsweredForms && !this.extendedSearchMode;
    }

    protected showAnswersNestedList(): boolean {
        return this.filterType === SideListOptionType.AnsweredForms && !this.extendedSearchMode;
    }

    protected readonly FiProfileSizes = FiProfileSizes;
}
